import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import ChatIcon from "@mui/icons-material/Forum";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { Can } from "../../components/Can";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const formatTime = value => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const chatTitle = chat =>
  chat?.title || chat?.name || chat?.user?.name || "Usuário";

const InternalChatPage = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [body, setBody] = useState("");
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupUserIds, setGroupUserIds] = useState([]);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const messagesEndRef = useRef(null);
  const selectedChatIdRef = useRef(null);
  const showAllRef = useRef(false);

  useEffect(() => {
    selectedChatIdRef.current = selectedChat?.id || null;
  }, [selectedChat]);

  useEffect(() => {
    showAllRef.current = showAll;
  }, [showAll]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const upsertChat = useCallback(chat => {
    if (!chat?.id) return;
    setChats(prev => {
      const without = prev.filter(item => item.id !== chat.id);
      return [chat, ...without].sort((a, b) => {
        const aTime = new Date(a.lastMessageAt || a.updatedAt || 0).getTime();
        const bTime = new Date(b.lastMessageAt || b.updatedAt || 0).getTime();
        return bTime - aTime;
      });
    });
  }, []);

  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, chatsRes] = await Promise.all([
        api.get("/internal-chats/users"),
        api.get("/internal-chats", {
          params: showAll ? { showAll: "true" } : undefined,
        }),
      ]);
      setUsers(usersRes.data || []);
      setChats(chatsRes.data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [showAll]);

  const loadMessages = useCallback(async chatId => {
    if (!chatId) return;
    setLoadingMessages(true);
    try {
      const { data } = await api.get(`/internal-chats/${chatId}/messages`);
      setMessages(data.messages || []);
      await api.put(`/internal-chats/${chatId}/read`);
      setChats(prev =>
        prev.map(item =>
          item.id === chatId ? { ...item, unreadCount: 0 } : item
        )
      );
      setTimeout(scrollToBottom, 50);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    loadBase();
  }, [loadBase]);

  useEffect(() => {
    if (!user?.id) return undefined;

    const socket = openSocket();
    const onConnect = () => socket.emit("joinUser", user.id);

    const onInternalChat = data => {
      if (!data?.chat) return;

      if (data.broadcastAdmin && !showAllRef.current) {
        return;
      }

      upsertChat(data.chat);

      if (
        data.action === "message" &&
        data.message &&
        selectedChatIdRef.current === data.chat.id
      ) {
        setMessages(prev => {
          if (prev.some(item => item.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
        setTimeout(scrollToBottom, 30);
        if (data.chat.viewerMode !== "admin") {
          api.put(`/internal-chats/${data.chat.id}/read`).catch(() => {});
        }
      }

      if (
        data.action === "update" &&
        selectedChatIdRef.current === data.chat.id
      ) {
        setSelectedChat(prev => (prev ? { ...prev, ...data.chat } : data.chat));
      }
    };

    onConnect();
    socket.on("connect", onConnect);
    socket.on("internalChat", onInternalChat);

    return () => {
      socket.off("connect", onConnect);
      socket.off("internalChat", onInternalChat);
    };
  }, [user?.id, upsertChat]);

  const openChatWithUser = async otherUser => {
    try {
      const { data } = await api.post("/internal-chats", { userId: otherUser.id });
      upsertChat(data);
      setSelectedChat(data);
      await loadMessages(data.id);
    } catch (err) {
      toastError(err);
    }
  };

  const openExistingChat = async chat => {
    setSelectedChat(chat);
    await loadMessages(chat.id);
  };

  const isAdminViewOnly = selectedChat?.viewerMode === "admin";
  const isGroupChat = Boolean(selectedChat?.isGroup);

  const handleSend = async () => {
    const text = body.trim();
    if (!text || !selectedChat?.id || sending || isAdminViewOnly) return;
    setSending(true);
    try {
      const { data } = await api.post(
        `/internal-chats/${selectedChat.id}/messages`,
        { body: text }
      );
      setBody("");
      if (data?.message) {
        setMessages(prev => {
          if (prev.some(item => item.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
      }
      if (data?.chat) {
        upsertChat(data.chat);
        setSelectedChat(data.chat);
      }
      setTimeout(scrollToBottom, 30);
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const toggleGroupUser = userId => {
    setGroupUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleCreateGroup = async () => {
    const name = groupName.trim();
    if (name.length < 2) {
      toast.error("Informe um nome para o grupo.");
      return;
    }
    if (groupUserIds.length < 1) {
      toast.error("Selecione ao menos um participante.");
      return;
    }

    setCreatingGroup(true);
    try {
      const { data } = await api.post("/internal-chats/groups", {
        isGroup: true,
        name,
        userIds: groupUserIds,
      });
      upsertChat(data);
      setSelectedChat(data);
      await loadMessages(data.id);
      setGroupOpen(false);
      setGroupName("");
      setGroupUserIds([]);
      toast.success("Grupo criado.");
    } catch (err) {
      toastError(err);
    } finally {
      setCreatingGroup(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter(
      item =>
        item.name?.toLowerCase().includes(term) ||
        item.email?.toLowerCase().includes(term)
    );
  }, [users, search]);

  const subtitle = useMemo(() => {
    if (!selectedChat) return "";
    if (isAdminViewOnly) return "Visualização administrativa";
    if (selectedChat.isGroup) {
      const names = (selectedChat.participants || [])
        .map(p => p.name)
        .filter(Boolean);
      return names.length
        ? `${names.length} participantes · ${names.slice(0, 4).join(", ")}${
            names.length > 4 ? "…" : ""
          }`
        : `${selectedChat.participantCount || 0} participantes`;
    }
    return selectedChat.user?.email || "";
  }, [selectedChat, isAdminViewOnly]);

  if (loading) {
    return (
      <MainContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress color="primary" />
        </Box>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Chat interno</Title>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: "auto" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<GroupAddIcon />}
            onClick={() => setGroupOpen(true)}
          >
            Novo grupo
          </Button>
          <Can
            role={user.profile}
            perform="internal-chat:showall"
            yes={() => (
              <FormControlLabel
                sx={{ mr: 0 }}
                control={
                  <Switch
                    color="primary"
                    checked={showAll}
                    onChange={e => {
                      setSelectedChat(null);
                      setMessages([]);
                      setShowAll(e.target.checked);
                    }}
                  />
                }
                label="Todas as conversas"
              />
            )}
          />
        </Stack>
      </MainHeader>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
          gap: 2,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar usuário"
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Divider />
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {showAll ? "Todas as conversas" : "Conversas"}
            </Typography>
          </Box>
          <List dense sx={{ overflowY: "auto", maxHeight: 220, py: 0 }}>
            {chats.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                Nenhuma conversa ainda.
              </Typography>
            )}
            {chats.map(chat => (
              <ListItemButton
                key={chat.id}
                selected={selectedChat?.id === chat.id}
                onClick={() => openExistingChat(chat)}
              >
                <ListItemAvatar>
                  <Badge
                    color="error"
                    badgeContent={chat.unreadCount || 0}
                    invisible={!chat.unreadCount}
                  >
                    <Avatar>
                      {chat.isGroup ? (
                        <GroupsIcon fontSize="small" />
                      ) : (
                        (chatTitle(chat)?.[0] || "?").toUpperCase()
                      )}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={chatTitle(chat)}
                  secondary={
                    chat.isGroup
                      ? `${chat.participantCount || 0} participantes · ${
                          chat.lastMessage || "Sem mensagens"
                        }`
                      : chat.lastMessage || "Sem mensagens"
                  }
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
          <Divider />
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Usuários
            </Typography>
          </Box>
          <List dense sx={{ overflowY: "auto", flex: 1, py: 0 }}>
            {filteredUsers.map(item => (
              <ListItemButton key={item.id} onClick={() => openChatWithUser(item)}>
                <ListItemAvatar>
                  <Avatar>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={item.name}
                  secondary={item.email}
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {!selectedChat ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                color: "text.secondary",
                p: 3,
              }}
            >
              <ChatIcon sx={{ fontSize: 48, opacity: 0.5 }} />
              <Typography variant="h6">Chat interno</Typography>
              <Typography variant="body2" textAlign="center">
                Converse 1:1 com um usuário ou crie um grupo para atendimento interno.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {isGroupChat && <GroupsIcon fontSize="small" color="primary" />}
                  <Typography variant="subtitle1" fontWeight={600}>
                    {chatTitle(selectedChat)}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              </Box>

              <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                {loadingMessages ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <Stack spacing={1.25}>
                    {messages.map(message => {
                      const mine = !isAdminViewOnly && message.senderId === user.id;
                      const showSender =
                        isAdminViewOnly || (isGroupChat && !mine);
                      return (
                        <Box
                          key={message.id}
                          sx={{
                            alignSelf: mine ? "flex-end" : "flex-start",
                            maxWidth: "75%",
                            bgcolor: mine ? "primary.main" : "action.hover",
                            color: mine ? "primary.contrastText" : "text.primary",
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                          }}
                        >
                          {showSender && (
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, display: "block" }}
                            >
                              {message.sender?.name || "Usuário"}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {message.body}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ opacity: 0.75, display: "block", mt: 0.5 }}
                          >
                            {formatTime(message.createdAt)}
                          </Typography>
                        </Box>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </Stack>
                )}
              </Box>

              <Box sx={{ p: 1.5, borderTop: 1, borderColor: "divider" }}>
                {isAdminViewOnly ? (
                  <Typography variant="body2" color="text.secondary">
                    Modo admin: apenas visualização desta conversa.
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Escreva uma mensagem..."
                      value={body}
                      onChange={e => setBody(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      multiline
                      maxRows={4}
                    />
                    <IconButton
                      color="primary"
                      onClick={handleSend}
                      disabled={sending || !body.trim()}
                    >
                      {sending ? <CircularProgress size={20} /> : <SendIcon />}
                    </IconButton>
                  </Stack>
                )}
              </Box>
            </>
          )}
        </Paper>
      </Box>

      <Dialog
        open={groupOpen}
        onClose={() => !creatingGroup && setGroupOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Novo grupo interno</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            label="Nome do grupo"
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
            margin="dense"
            placeholder="Ex.: Operações, Suporte N2..."
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Participantes ({groupUserIds.length})
          </Typography>
          <List dense sx={{ maxHeight: 280, overflowY: "auto" }}>
            {users.map(item => (
              <ListItemButton
                key={item.id}
                onClick={() => toggleGroupUser(item.id)}
                dense
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge="start"
                    checked={groupUserIds.includes(item.id)}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={item.name} secondary={item.email} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupOpen(false)} disabled={creatingGroup}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={creatingGroup}
            startIcon={
              creatingGroup ? <CircularProgress size={16} color="inherit" /> : <GroupAddIcon />
            }
          >
            Criar grupo
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default InternalChatPage;
